/**
 * La scheda Cronaca della partita conclusa: gli eventi minuto per minuto in
 * una tabella compatta come il tabellino, divisa per tempo. Una riga per
 * voce: minuto, icona, evento, giocatore (per le sostituzioni chi esce e chi
 * entra) e il punteggio sulle voci che lo cambiano, prima la squadra di casa.
 */

import type { Tabellino as DatiTabellino } from '@calcio/shared';
import { ArrowDownIcon, ArrowUpIcon, ClockIcon, ListOrderedIcon } from 'lucide-react';
import { Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CardVista } from '../comuni/Riquadri.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';
import { usePartita } from './contesto.js';
import { IconaEvento } from './eventi.js';
import { ETICHETTA_EVENTO, type NomeGiocatore, nomeBreve, punteggio } from './partita.js';

type Voce = DatiTabellino['voci'][number];

export function SchedaCronaca() {
  const partita = usePartita();
  if (!partita?.tabellino) return <StatoCaricamento errore={partita?.errore ?? null} />;
  const { tabellino, giocatori, partita: p } = partita.tabellino;
  const voci = tabellino.voci;

  return (
    <CardVista titolo="Cronaca" Icona={ListOrderedIcon} conteggio={voci.length}>
      {voci.length === 0 ? (
        <StatoVuoto titolo="Nessun evento registrato" />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                {/* Larga quanto il minuto più lungo: `w-px` la stringe al contenuto. */}
                <TableHead className="w-px">
                  <ClockIcon aria-label="Minuto" className="size-4" />
                </TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Giocatore</TableHead>
                <TableHead className="text-right">Risultato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {voci.map((v, i) => (
                <Fragment key={v.eventoId}>
                  {v.periodo !== voci[i - 1]?.periodo && (
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableCell
                        colSpan={4}
                        className="py-1.5 text-[12px] font-semibold tracking-wide text-muted-foreground uppercase"
                      >
                        {v.periodo}° tempo
                      </TableCell>
                    </TableRow>
                  )}
                  <RigaCronaca voce={v} giocatori={giocatori} inCasa={p.inCasa} />
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardVista>
  );
}

function RigaCronaca({
  voce: v,
  giocatori,
  inCasa,
}: {
  voce: Voce;
  giocatori: NomeGiocatore[];
  inCasa: boolean;
}) {
  const nome = (id: string | null | undefined) =>
    nomeBreve(id ? giocatori.find((g) => g.id === id) : undefined);
  // Le righe che cambiano il punteggio si leggono a colpo d'occhio.
  return (
    <TableRow className={cn(v.risultatoDopo && 'font-semibold')}>
      <TableCell className="font-semibold tabular-nums">{v.etichetta}</TableCell>
      <TableCell>
        <span className="flex items-center gap-2">
          <IconaEvento tipo={v.tipo} />
          {v.tipo === 'sostituzione' ? 'Sostituzione' : ETICHETTA_EVENTO[v.tipo]}
        </span>
      </TableCell>
      <TableCell>
        {v.tipo === 'sostituzione' ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {v.giocatoreUscenteId && (
              <span className="inline-flex items-center gap-1">
                <ArrowDownIcon aria-label="Esce" className="size-3.5 text-destructive" />
                {nome(v.giocatoreUscenteId)}
              </span>
            )}
            {v.giocatoreId && (
              <span className="inline-flex items-center gap-1">
                <ArrowUpIcon aria-label="Entra" className="size-3.5 text-stato-disponibile" />
                {nome(v.giocatoreId)}
              </span>
            )}
          </span>
        ) : v.giocatoreId ? (
          nome(v.giocatoreId)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {v.risultatoDopo && (
          <Badge variant="outline" className="tabular-nums">
            {punteggio(v.risultatoDopo, inCasa, '–')}
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
